<template>
    <div class="country-language-selection" :data-testid="dataTestid">
        <div class="country-language-selection__item">
            <VJoySelect
                :model-value="selectedCountryCode"
                :label="countryLabel"
                :data-testid="`${dataTestid}--country-select`"
                @update:model-value="handleCountryUpdate"
            >
                <option v-for="host in supportedHosts" :key="`country-${host.countryCode}`" :value="host.countryCode">
                    {{ host.countryName }}
                </option>
            </VJoySelect>
        </div>
        <div class="country-language-selection__item">
            <VJoySelect
                :model-value="selectedLanguageCode"
                :label="languageLabel"
                :data-testid="`${dataTestid}--language-select`"
                @update:model-value="handleLanguageUpdate"
            >
                <option v-for="language in selectedHost?.languages ?? []" :key="`language-${language.language}`" :value="language.language">
                    {{ formatLanguageName(language) }}
                </option>
            </VJoySelect>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {LanguageName, type CountryAndLanguageSelectionUpdateEvent, type Host} from '@navbar-unlogged-types';
    import {useAsyncData, useFormat, useHosts, useLocale, useTranslation} from '#imports';
    import {VJoySelect} from '@maltjoy/core-vue';

    interface Props {
        dataTestid: string;
        countryLabel: string;
        languageLabel: string;
    }

    const {dataTestid, countryLabel, languageLabel} = defineProps<Props>();

    const emit = defineEmits<{
        (e: 'update:countryCode', countryCode: string, maybeSelectedInfos: CountryAndLanguageSelectionUpdateEvent): void;
        (e: 'update:languageCode', languageCode: string, maybeSelectedInfos: CountryAndLanguageSelectionUpdateEvent): void;
    }>();

    const {getHosts} = useHosts();
    const {countryCode: currentCountryCode, languageCode: currentLanguageCode, isMultiLocaleHost} = useLocale();
    const {formatCountryName, formatLanguageName} = useFormat();
    const {t} = useTranslation();

    const selectedHost = ref<Host | undefined>();
    const selectedCountryCode = ref(currentCountryCode.value ?? '');
    const selectedLanguageCode = ref(currentLanguageCode.value ?? '');

    const {data} = await useAsyncData(
        'country-and-language-selection',
        async () => {
            const sortedHosts = buildSupportedHosts((await getHosts()) as Host[]);
            selectedHost.value = sortedHosts.find((host) => host.countryCode === currentCountryCode.value) ?? sortedHosts[0];
            return {sortedHosts};
        },
        {deep: true},
    );

    const supportedHosts = computed<Host[]>(() => data.value?.sortedHosts ?? []);

    function handleCountryUpdate(countryCode: string) {
        selectedCountryCode.value = countryCode;
        selectedHost.value = supportedHosts.value.find((host) => host.countryCode === countryCode);
        selectedLanguageCode.value = languageCodeForHost(selectedHost.value, selectedLanguageCode.value);
        emit('update:countryCode', countryCode, getSelectedInfos());
    }

    function handleLanguageUpdate(languageCode: string) {
        selectedLanguageCode.value = languageCode;
        emit('update:languageCode', languageCode, getSelectedInfos());
    }

    function getSelectedInfos(): CountryAndLanguageSelectionUpdateEvent {
        const maybeSelectedLanguage = selectedHost.value?.languages.find((language) => language.language === selectedLanguageCode.value);

        return {
            maybeSelectedLocale: maybeSelectedLanguage?.locale,
            maybeSelectedHost: selectedHost.value,
            maybeSelectedLanguage,
        };
    }

    function buildSupportedHosts(hosts: Host[]): Host[] {
        const countryHosts = hosts
            .filter((host) => host.countryName && host.countryCode)
            .map((host) => ({
                ...host,
                countryName: formatCountryName(host),
            }))
            .sort((a, b) => a.countryName.localeCompare(b.countryName));

        if (!isMultiLocaleHost.value) {
            return countryHosts;
        }

        return [...countryHosts, getMaltComHost(hosts)];
    }

    function getMaltComHost(hosts: Host[]): Host {
        const maltComHost = hosts.find((host) => !host.countryName && !host.countryCode) ?? createFallbackMaltComHost();
        maltComHost.countryName = t('navbar.feat.locale-selection.dialog.malt-com.country.select.option');
        return maltComHost;
    }

    function createFallbackMaltComHost(): Host {
        return {
            countryName: '',
            countryCode: '',
            languages: [
                {
                    host: 'malt.com',
                    language: LanguageName.EN,
                    locale: 'en',
                    label: 'English',
                },
            ],
        };
    }

    function languageCodeForHost(host: Host | undefined, currentLanguage: string): string {
        if (!host) {
            return currentLanguage;
        }

        return host.languages.find((language) => language.language === currentLanguage)?.language ?? host.languages[0]?.language ?? '';
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    /* Responsive column → row layout: VJoyWrapper has no breakpoint-aware direction. */
    .country-language-selection {
        display: flex;
        flex-direction: column;
        gap: var(--joy-core-spacing-6);
        justify-content: stretch;

        &__item {
            width: 100%;
            flex: 1;
        }

        @include mq.screen-sm(min) {
            flex-direction: row;

            &__item {
                width: 50%;
            }
        }
    }
</style>
